import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import {NgbDate, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {RouteService} from '../../shared/services/route.service';
import {Stop} from '../../shared/models/stop.model';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {Route} from '../../shared/models/route.model';
import {DataStorageService} from '../../shared/services/data-storage.service';
import {RouteGroup} from '../../shared/models/routeGroup.model';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {ToolsService} from '../../shared/services/tools.service';

import swal from 'sweetalert2';
import {SendEmailService} from '../../shared/services/send-email.service';

@Component({
    selector: 'form-edit-route',
    templateUrl: 'form-edit-route.html',
    styleUrls: ['../../../assets/scss/plugins/_datepicker.scss', './form-edit-route.scss']
})

export class FormEditRouteComponent implements OnInit, OnDestroy {

    @Input() routes: Route[];

    routeForm: FormGroup;
    stops: Stop[];
    routeGroups: RouteGroup[];
    routeGroupOptions: any = [];
    pickStopOptions: any = [];
    dropStopOptions: any = [];

    selectConfig: any = {
        labelField: 'label',
        valueField: 'value',
        searchField: ['label']
    };

    stopSelectConfig: any = {
        labelField: 'label',
        valueField: 'value',
        searchField: ['label']
    };

    date: { year: number, month: number };

    is_please_wait: boolean;

    other_new_stop_changed = new Subject<number>();

    private unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        private routeService: RouteService,
        private dataStorageService: DataStorageService,
        private ngDateParserFormatter: NgbDateParserFormatter,
        private sendEmailService: SendEmailService,
        private toolsService: ToolsService) {
        this.is_please_wait = false;
    }

    ngOnInit() {

        this.stops = this.routeService.getStop();
        this.routeGroups = this.routeService.getRouteGroup();
        this.initRouteGroupOptions();
        this.initStopOptions();
        this.initForm();

        this.routeService.stopsChanged
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            (stops: Stop[]) => {
                this.stops = this.routeService.getStop();
                this.initStopOptions();
            }
        );

        this.routeService.routeGroupsChanged
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            (routeGroups: RouteGroup[]) => {
                this.routeGroups = routeGroups;
                this.initRouteGroupOptions();
            }
        );
    }

    initForm() {

        const routes = new FormArray([]);
        if (this.routes) {
            for (const route of this.routes) {

                const start_date = new NgbDate(
                    +route.start_date.substring(0, 4),
                    +route.start_date.substring(5, 7),
                    +route.start_date.substring(8, 10));

                const end_date = new NgbDate(
                    +route.end_date.substring(0, 4),
                    +route.end_date.substring(5, 7),
                    +route.end_date.substring(8, 10));

                routes.push(
                    new FormGroup({
                        'pick_stop_id': new FormControl(route.pick_stop_id),
                        'drop_stop_id': new FormControl(route.drop_stop_id),
                        'pick_time': new FormControl(route.pick_time),
                        'drop_time': new FormControl(route.drop_time),
                        'start_date': new FormControl(start_date),
                        'end_date': new FormControl(end_date),
                        'adult_price': new FormControl(route.adult_price),
                        'child_price': new FormControl(route.child_price),
                        'infant_price': new FormControl(route.infant_price),
                        'route_group_id': new FormControl(route.route_group_id)
                    })
                );
            }
        }

        this.routeForm = new FormGroup({
            'routes': routes
        });
    }

    getControls() {
        return (<FormArray>this.routeForm.get('routes')).controls;
    }

    initRouteGroupOptions() {
        for (const routeGroup of this.routeGroups) {
            this.routeGroupOptions.push({'label': routeGroup.title, 'value': routeGroup.id, 'code': routeGroup.title});
        }
    }

    initStopOptions() {
        for (const stop of this.stops) {
            this.pickStopOptions.push({'label': stop.title, 'value': stop.id, 'code': stop.title});
            this.dropStopOptions.push({'label': stop.title, 'value': stop.id, 'code': stop.title});
        }
    }

    onEditRoutes() {
        const value = this.routeForm.value.routes;

        let i = 0;
        for (const route of this.routes) {

            const value_start_date = this.ngDateParserFormatter.format(value[i].start_date);
            const value_end_date = this.ngDateParserFormatter.format(value[i].end_date);

            let is_changed = false;
            let is_end_date_reduced = false; // if true, have to send bulk notification emails.
            if ((value[i].pick_stop_id !== route.pick_stop_id) ||
                (value[i].drop_stop_id !== route.drop_stop_id) ||
                (value[i].pick_time !== route.pick_time) ||
                (value[i].drop_time !== route.drop_time) ||
                (value_start_date !== route.start_date) ||
                (value_end_date !== route.end_date) ||
                (value[i].route_group_id !== route.route_group_id) ||
                (value[i].adult_price !== route.adult_price) ||
                (value[i].child_price !== route.child_price) ||
                (value[i].infant_price !== route.infant_price)
            ) {
                is_changed = true;
            }
            if (value_end_date < route.end_date) {
                is_end_date_reduced = true;
            }

            const newRoute =
                new Route(route.id, value[i].pick_stop_id, value[i].drop_stop_id,
                    value[i].pick_time, value[i].drop_time,
                    value_start_date, value_end_date, +value[i].adult_price,
                    +value[i].child_price,+value[i].infant_price, value[i].route_group_id);

            if (is_changed && is_end_date_reduced) {

                let effected_reservations = [];
                // send bulk email to passengers who already have this route.
                this.dataStorageService.fetchReservationListsByRoute(route.id, route.end_date, value_end_date)
                    .pipe(takeUntil(this.unsubscribe$)).subscribe(
                    reservations => {
                        if (reservations) {
                            effected_reservations = reservations;
                            let changing_stop = 0;
                            // find the next stop
                            if (route.route_group_id % 2 !== 0) {
                                // ob
                                changing_stop = route.pick_stop_id;
                            } else {
                                // ib
                                changing_stop = route.drop_stop_id;
                            }
                            let k = 0;
                            let index = 0;
                            for (const stop of this.stops) {
                                if (stop.id === changing_stop) {
                                    // last(except airport) or first?
                                    if (i !== this.stops.length - 2 || i === 0) {
                                        index = k + 1;
                                    } else {
                                        index = k - 1;
                                    }
                                }
                                k++;
                            }
                            const new_stop_id = this.stops[index].id;

                            if (new_stop_id) {
                                // check if change and send notification to customers
                                swal({
                                    title: 'Are you sure?',
                                    text: 'It affects ' + effected_reservations.length
                                        + 'reservation(s). Would you like to change the stop to '
                                        + this.routeService.getStopTitleOne(new_stop_id) + '?',
                                    showCancelButton: true,
                                    confirmButtonColor: '#2dce89',
                                    cancelButtonColor: '#f5365c',
                                    confirmButtonText: 'Yes, Proceed',
                                    cancelButtonText: 'No, I don\'t want it!'
                                }).then((result) => {
                                    // change related reservations ticket routes and send notification email.
                                    // and update end_date of the route.
                                    if (result.value) {
                                        this.is_please_wait = true;
                                        // find new route with new stop
                                        const new_route_id = this.findNewRoute(new_stop_id, route.route_group_id);
                                        // get ticket ids
                                        const ticket_ids = this.findTicketIds(reservations, route.id);
                                        // update tickets with new route_id
                                        this.dataStorageService.updateTicketsRoute(ticket_ids, new_route_id)
                                            .pipe(takeUntil(this.unsubscribe$)).subscribe(
                                            res => {
                                                if (res['error']) {
                                                    alert('Updating Reservation Ticket Routes Failed. Please try again. ');
                                                } else {
                                                    // send bulk email to customers.
                                                    const new_time = this.routeService.getRouteTimeById(new_route_id);
                                                    this.sendEmailToPassengers(reservations
                                                        , this.routeService.getStopTitleOne(new_stop_id), new_time);
                                                    // update route
                                                    this.updateRoute(newRoute);
                                                }
                                            }
                                        );
                                    } else {
                                        // change to another stop? or cancel changing?
                                        swal({
                                            title: 'What do you want?',
                                            text: 'Would you like to change the stop to another stop or cancel changing?',
                                            showCancelButton: true,
                                            confirmButtonColor: '#3085d6',
                                            cancelButtonColor: '#f5365c',
                                            confirmButtonText: 'Yes, Another Stop!',
                                            cancelButtonText: 'No, Just Cancel!'
                                        }).then((result2) => {
                                            if (result2.value) {
                                                // change to another stop
                                                this.findNewStop([new_stop_id, changing_stop]);
                                                this.other_new_stop_changed
                                                    .pipe(takeUntil(this.unsubscribe$)).subscribe(
                                                    other_new_stop_id => {
                                                        // change related reservations ticket routes and send notification email.
                                                        // and update end_date of the route.
                                                        this.is_please_wait = true;
                                                        // find new route with new stop
                                                        const new_route_id = this.findNewRoute(other_new_stop_id, route.route_group_id);
                                                        // get ticket ids
                                                        const ticket_ids = this.findTicketIds(reservations, route.id);
                                                        // update tickets with new route_id
                                                        this.dataStorageService.updateTicketsRoute(ticket_ids, new_route_id)
                                                            .pipe(takeUntil(this.unsubscribe$)).subscribe(
                                                            res => {
                                                                if (res['error']) {
                                                                    alert('Updating Reservation Ticket Routes Failed. Please try again. ');
                                                                } else {
                                                                    // send bulk email to customers.
                                                                    // send to info and bcc customers.
                                                                    const new_time = this.routeService.getRouteTimeById(new_route_id);
                                                                    this.sendEmailToPassengers(reservations
                                                                        , this.routeService.getStopTitleOne(other_new_stop_id), new_time);
                                                                    // update route
                                                                    this.updateRoute(newRoute);
                                                                }
                                                            }
                                                        );
                                                    }
                                                );
                                            } else {
                                                alert('Changes may not be saved. ');
                                                location.reload();
                                            }
                                        });
                                    }
                                });
                            }
                        } else {
                            this.is_please_wait = true;
                            this.updateRoute(newRoute);
                        }
                    }
                );
            }
            if (is_changed && !is_end_date_reduced) {
                this.is_please_wait = true;
                this.updateRoute(newRoute);
            }
            i++;
        }

    }

    sendEmailToPassengers(reservations, new_stop, new_time) {
        for (const reservation of reservations) {
            const emailObj = {
                type: 8,
                email_to: reservation.email,
                reservation: reservation,
                new_stop: new_stop,
                new_time: new_time
            };
            this.sendEmailService.initEmail(emailObj);
        }
    }

    findTicketIds(reservations, route_id) {
        const ticket_ids = [];
        let ticket_objs = [];
        reservations.map(x => {
            ticket_objs.push(x.ticket);
            if (x.ticket_re) {
                ticket_objs.push(x.ticket);
            }
        });
        ticket_objs = ticket_objs.filter(x => x.route_id === route_id);
        ticket_objs.map(
            x => {
                ticket_ids.push(x.id);
            }
        );
        return ticket_ids;
    }

    updateRoute(newRoute) {
        this.dataStorageService.updateRoute(newRoute)
            .pipe(takeUntil(this.unsubscribe$)).subscribe(
            response => {
                if (response['error']) {
                    alert('updating route error');
                } else {
                    console.log(response);
                    this.is_please_wait = false;
                    this.dataStorageService.fetchRoutes().subscribe();
                }
            });
    }

    findNewRoute(new_stop_id: number, route_group_id: number) {
        if (route_group_id % 2 !== 0) {
            // ob
            return this.routes.filter(x => x.pick_stop_id === new_stop_id)[0].id;
        } else {
            // ib
            return this.routes.filter(x => x.drop_stop_id === new_stop_id)[0].id;
        }
    }

    findNewStop(except_stops: number[]) {
        const available_stop_objs = this.stops.filter(x => !(x.id in except_stops));
        const available_stop_ids = [];
        available_stop_objs.map(x => {
            available_stop_ids.push(x.id);
        });
        const rand_stop_id_index = Math.floor(Math.random() * available_stop_ids.length);
        const rand_stop = this.stops[rand_stop_id_index];
        // ask which stops admin wants to have.
        swal({
            title: 'What do you want?',
            text: 'Would you like to change the stop/drop stop to ' + rand_stop.title + '?',
            showCancelButton: true,
            confirmButtonColor: '#2dce89',
            cancelButtonColor: '#f5365c',
            confirmButtonText: 'Yes, ' + rand_stop.title + '!',
            cancelButtonText: 'No, Another Stop!'
        }).then((result3) => {
            if (result3.value) {
                // change to another stop
                this.other_new_stop_changed.next(rand_stop.id);
            } else {
                this.findNewStop(except_stops);
            }
        });
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
